(() => {
  'use strict';
  if (window.CorlynaRewardsCheckout) return;

  // Keep the original YX request client and payment routes. The verified
  // Storefront cart is the amount authority; this module never invents a price.
  const creationPaths = new Set([
    '/api/checkout', '/api/checkout/credit', '/api/checkout/paypal',
    '/api/checkout/google', '/api/checkout/apple'
  ]);
  const fetchStorefront = window.fetch.bind(window);
  const own = (o, key) => Object.prototype.hasOwnProperty.call(o, key);
  const object = value => value && typeof value === 'object' && !Array.isArray(value);
  const idOf = value => String(value || '').split('/').pop();
  const gid = (kind, value) => `gid://shopify/${kind}/${idOf(value)}`;
  const variantOf = line => idOf(line.variant_id || line.variantId || line.merchandise?.id || line.id);
  const planOf = line => idOf(line.selling_plan_allocation?.selling_plan?.id || line.sellingPlanAllocation?.sellingPlan?.id || line.sellingPlanId);
  const quantityOf = line => Number(line.quantity);
  const codesOf = input => [...new Map((Array.isArray(input) ? input : String(input || '').split(','))
    .map(value => String(typeof value === 'string' ? value : value?.code || '').trim())
    .filter(Boolean).map(code => [code.toUpperCase(), code])).values()];
  const attributesOf = line => Array.isArray(line.attributes)
    ? line.attributes.map(({ key, value }) => ({ key: String(key), value: String(value) }))
    : Object.entries(line.properties || {}).filter(([, value]) => value != null)
      .map(([key, value]) => ({ key, value: String(value) }));
  const stableAttributes = attributes => JSON.stringify([...attributes].sort((a, b) => a.key.localeCompare(b.key) || a.value.localeCompare(b.value)));
  const identity = line => [variantOf(line), planOf(line), stableAttributes(attributesOf(line))].join('|');
  const logicalIdentity = line => [variantOf(line), planOf(line)].join('|');
  const messages = {
    NOT_READY: 'Rewards are still loading. Your items are saved; please try checkout again.',
    BUSY: 'Your checkout rewards are being verified. Please wait a moment.',
    CART_CHANGED: 'Your cart changed while checkout was starting. Your items are saved; please try again.',
    CART: 'We could not confirm your cart rewards. Your items are saved; please refresh your cart before checkout.',
    GIFT: 'We could not confirm your free gift. Your items are saved; please refresh your cart before checkout.',
    SHIPPING: 'We could not confirm free shipping. Your items are saved; please try again before paying.',
    CHECKOUT: 'We could not confirm your checkout total. Your items are saved; please try again before paying.'
  };
  function fail(code) {
    const error = new Error(messages[code] || messages.CHECKOUT);
    error.name = 'CorlynaRewardsError';
    error.code = code;
    throw error;
  }
  function showError(error) {
    const code = own(messages, error?.code) ? error.code : 'CHECKOUT';
    const message = messages[code];
    const hosts = [...document.querySelectorAll('#CartDrawer-CartErrors, #cart-errors, [data-corlyna-rewards]')];
    if (!hosts.length) {
      const host = document.querySelector('.main-product__info-container, main');
      if (host) hosts.push(host);
    }
    for (const host of hosts) {
      let notice = host.querySelector('[data-corlyna-rewards-checkout-error]');
      if (!notice) {
        notice = document.createElement('p');
        notice.dataset.corlynaRewardsCheckoutError = '';
        notice.setAttribute('role', 'alert');
        host.appendChild(notice);
      }
      notice.textContent = message;
    }
    window.dispatchEvent(new CustomEvent('corlyna:rewards-checkout-error', { detail: { code, message } }));
  }
  function clearError() {
    document.querySelectorAll('[data-corlyna-rewards-checkout-error]').forEach(node => node.remove());
  }
  function sumQuantities(lines, key = identity) {
    const map = new Map();
    for (const line of lines) {
      const quantity = quantityOf(line);
      if (!Number.isSafeInteger(quantity) || quantity <= 0 || !variantOf(line)) fail('CART');
      const signature = key(line);
      map.set(signature, (map.get(signature) || 0) + quantity);
    }
    return map;
  }
  function sameQuantities(a, b, key = identity) {
    const left = sumQuantities(a, key), right = sumQuantities(b, key);
    return left.size === right.size && [...left].every(([signature, quantity]) => right.get(signature) === quantity);
  }
  function monetary(value, currency, code = 'CART') {
    if (!object(value) || value.currencyCode !== currency || typeof value.amount !== 'string' || !/^\d+(?:\.\d+)?$/.test(value.amount)) fail(code);
    const amount = Number(value.amount);
    if (!Number.isFinite(amount) || amount < 0) fail(code);
    return amount;
  }
  function near(a, b) { return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 0.005; }
  const moneyFields = 'amount currencyCode';
  const cartFields = `id totalQuantity note attributes { key value }
    discountCodes { code applicable }
    discountAllocations { discountedAmount { ${moneyFields} } ... on CartCodeDiscountAllocation { code } }
    cost { subtotalAmount { ${moneyFields} } totalAmount { ${moneyFields} } totalTaxAmount { ${moneyFields} } }
    lines(first: $first) { pageInfo { hasNextPage } nodes {
      id quantity attributes { key value }
      merchandise { ... on ProductVariant { id title requiresShipping weight weightUnit
        price { ${moneyFields} } image { url altText } product { id title handle featuredImage { url altText } } } }
      sellingPlanAllocation { sellingPlan { id name } }
      cost { amountPerQuantity { ${moneyFields} } subtotalAmount { ${moneyFields} } totalAmount { ${moneyFields} } }
      discountAllocations { discountedAmount { ${moneyFields} } ... on CartCodeDiscountAllocation { code } }
    } }`;
  const createQuery = `mutation CorlynaRewardsCart($input: CartInput!, $first: Int!) {
    cartCreate(input: $input) { cart { ${cartFields} } userErrors { field message } warnings { code } }
  }`;
  const readQuery = `query CorlynaRewardsCartRead($id: ID!, $first: Int!) { cart(id: $id) { ${cartFields} } }`;

  function configuration() {
    const rewards = window.CorlynaRewards;
    const config = rewards?.getConfig?.() || rewards?.config;
    if (!rewards || !object(config)) fail('NOT_READY');
    return { rewards, config };
  }
  function applicableCodes(cart) {
    return codesOf((cart.discountCodes || []).filter(code => code.applicable === true));
  }
  function verifyStorefront(cart, inputLines, benefit, config, currency) {
    if (!object(cart) || !String(cart.id || '').startsWith('gid://shopify/Cart/') || cart.lines?.pageInfo?.hasNextPage || !Array.isArray(cart.lines?.nodes)) fail('CART');
    const lines = cart.lines.nodes;
    if (!sameQuantities(inputLines, lines) || cart.totalQuantity !== inputLines.reduce((sum, line) => sum + line.quantity, 0)) fail('CART');
    const total = monetary(cart.cost?.totalAmount, currency);
    const subtotal = monetary(cart.cost?.subtotalAmount, currency);
    const tax = cart.cost.totalTaxAmount == null ? 0 : monetary(cart.cost.totalTaxAmount, currency);
    for (const line of lines) {
      monetary(line.cost?.totalAmount, currency);
      monetary(line.cost?.subtotalAmount, currency);
      monetary(line.cost?.amountPerQuantity, currency);
      monetary(line.merchandise?.price, currency);
      if (!line.merchandise?.product?.id) fail('CART');
    }
    const applied = new Set(applicableCodes(cart).map(code => code.toUpperCase()));
    if (benefit.codes.some(code => !applied.has(code.toUpperCase()))) fail(benefit.gift ? 'GIFT' : 'SHIPPING');
    if (benefit.freeShipping && !benefit.codes.length || benefit.gift && !benefit.giftCode) fail('NOT_READY');
    let giftUnit = 0;
    if (benefit.gift) {
      const giftLines = lines.filter(line => variantOf(line) === String(config.gift.id));
      const key = config.gift.ownershipProperty, value = config.gift.ownershipValue;
      const owned = giftLines.filter(line => attributesOf(line).some(attribute => attribute.key === key && attribute.value === value));
      if (owned.length !== 1 || owned[0].quantity !== 1 || planOf(owned[0])) fail('GIFT');
      giftUnit = monetary(owned[0].merchandise.price, currency);
      if (giftUnit <= 0) fail('GIFT');
      // Shopify may allocate the free unit to a paid same-SKU line. Verify
      // the whole SKU and our own code, rather than deleting/repricing that line.
      const ownGiftDiscount = giftLines.reduce((sum, line) => sum + (line.discountAllocations || [])
        .filter(allocation => String(allocation.code || '').toUpperCase() === benefit.giftCode.toUpperCase())
        .reduce((allocated, allocation) => allocated + monetary(allocation.discountedAmount, currency), 0), 0);
      const giftQuantity = giftLines.reduce((sum, line) => sum + line.quantity, 0);
      const giftTotal = giftLines.reduce((sum, line) => sum + monetary(line.cost.totalAmount, currency), 0);
      if (!near(ownGiftDiscount, giftUnit) || giftTotal > (giftQuantity - 1) * giftUnit + 0.004) fail('GIFT');
    }
    return { cart, lines, total, subtotal, tax, currency, giftUnit, benefit };
  }

  async function prepareDirectPayload(body) {
    const { rewards, config } = configuration();
    await rewards.ready;
    const selected = body.cart.corlynaDirectSelection;
    if (!object(selected) || !config.variants?.[variantOf(selected)] ||
      !Number.isSafeInteger(selected.quantity) || selected.quantity < 1 || selected.quantity > 99 ||
      !sameQuantities(body.cart.items, [selected], logicalIdentity)) fail('CART');
    const nativeCart = await rewards.readCart();
    const benefit = rewards.assess([selected]);
    const currency = String(nativeCart.currency || '').toUpperCase();
    if (!/^[A-Z]{3}$/.test(currency)) fail('CART');
    const lines = [{ merchandiseId: gid('ProductVariant', variantOf(selected)), quantity: selected.quantity,
      attributes: attributesOf(selected), ...(planOf(selected) ? { sellingPlanId: gid('SellingPlan', planOf(selected)) } : {}) }];
    const input = { lines, discountCodes: benefit.codes,
      attributes: Object.entries(nativeCart.attributes || {}).map(([key, value]) => ({ key, value: String(value) })),
      ...(nativeCart.note ? { note: nativeCart.note } : {}),
      buyerIdentity: { countryCode: /^[A-Z]{2}$/.test(window.Shopify?.country || '') ? window.Shopify.country : 'US' } };
    // This independent Storefront cart never adds, clears, or reconciles the Ajax cart.
    return createAuthority(body, input, benefit, config, currency, [], nativeCart);
  }

  let completedDirectCart = null, directPending = false;
  function selectionFromProduct(button) {
    const scope = button.closest('.main-product__info-container');
    const form = scope?.querySelector('product-form.main-product-form form') || scope?.querySelector('form[action*="/cart/add"]');
    if (!form) fail('NOT_READY');
    const data = new FormData(form), variantId = idOf(data.get('id'));
    const quantity = Number(data.get('quantity') || 1);
    const formPlan = idOf(data.get('selling_plan'));
    const appPlan = typeof getSellingPlanId === 'function' ? idOf(getSellingPlanId()) : formPlan;
    if (!variantId || idOf(button.dataset.variantId) !== variantId || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > 99 ||
      formPlan && formPlan !== appPlan) fail('CART_CHANGED');
    const properties = {};
    for (const [key, value] of data) {
      const match = /^properties\[([^\]]+)\]$/.exec(key);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    return { variantId: gid('ProductVariant', variantId), quantity, properties,
      sellingPlanId: appPlan || null,
      sellingPlanGroupId: appPlan ? String(window.yx_product?.selling_plan_groups?.find(g =>
        g.selling_plans?.some(p => idOf(p.id) === appPlan))?.id || '') : '' };
  }
  async function directCheckout(button) {
    if (directPending || active) fail('BUSY');
    directPending = true; completedDirectCart = null;
    const disabled = button.disabled;
    try {
      clearError();
      const selected = selectionFromProduct(button), { config } = configuration();
      if (!config.enabled || !config.variants?.[variantOf(selected)] || typeof setCheckoutId !== 'function' ||
        typeof redirctToCheckout !== 'function' || typeof handlePaypalCheckout !== 'function') fail('NOT_READY');
      button.disabled = true; button.setAttribute('aria-busy', 'true');
      if (typeof showOverlay === 'function') showOverlay();
      const paypal = button.id === 'yx_paypal_checkout';
      await setCheckoutId({ items: [selected], corlynaDirectSelection: selected }, 'corlyna-direct-selection', paypal ? 'paypal' : 'none');
      if (!completedDirectCart) fail('CHECKOUT');
      const options = { skipCreateCart: true, clearFirst: false, cartToken: completedDirectCart.cartToken, restData: completedDirectCart };
      if (paypal) await handlePaypalCheckout('false', options);
      else if (typeof templateType !== 'undefined' && templateType === 'paypal_with_upsell') await handlePaypalCheckout('true', options);
      else await redirctToCheckout();
    } catch (error) {
      showError(error);
      if (typeof hideOverlay === 'function') hideOverlay();
    } finally {
      button.disabled = disabled; button.removeAttribute('aria-busy'); directPending = false; completedDirectCart = null;
    }
  }
  function captureProductCheckout(event) {
    const button = event.target?.closest?.('.yx-main-product-page #yx_checkout, .yx-main-product-page #yx_paypal_checkout');
    if (!button) return;
    let config;
    try { ({ config } = configuration()); }
    catch (error) { event.preventDefault(); event.stopImmediatePropagation(); showError(error); return; }
    if (!config.variants?.[idOf(button.dataset.variantId)]) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (button.disabled || directPending || active) return;
    void directCheckout(button);
  }
  async function preparePayload(body) {
    if (body.cart?.corlynaDirectSelection) return prepareDirectPayload(body);
    const { rewards, config } = configuration();
    await rewards.ready;
    const before = await rewards.readCart();
    if (body.cart.cartToken !== before.token) fail('CART_CHANGED');
    // YX drops properties while collecting its body. Compare every SKU and
    // plan, including cream, before restoring ownership from the Ajax cart.
    if (!sameQuantities(body.cart.items, before.items, logicalIdentity)) fail('CART_CHANGED');
    const beforeBenefit = rewards.assess(before.items);
    const ownCodes = new Set(rewards.ownedCodeNames().map(code => code.toUpperCase()));
    const activeCustomerCodes = cart => codesOf([
      ...(cart.discount_codes || []).filter(code => object(code) && code.applicable === true),
      ...(cart.cart_level_discount_applications || []).filter(discount => discount.type === 'discount_code')
        .map(discount => discount.code || discount.title)
    ]).filter(code => !ownCodes.has(code.toUpperCase()));
    const originalCustomerCodes = activeCustomerCodes(before);
    const beforeCodes = codesOf([...(before.discount_codes || []), ...(before.cart_level_discount_applications || [])
      .filter(discount => discount.type === 'discount_code').map(discount => discount.code || discount.title)]);
    if (!beforeBenefit.freeShipping && !beforeBenefit.gift && !before.items.some(rewards.isOwnedGift) &&
      !beforeCodes.some(code => ownCodes.has(code.toUpperCase()))) return { body, bypass: true };
    const result = await rewards.prepareForCheckout();
    const { cart: nativeCart, benefit } = result;
    if (!nativeCart || !Array.isArray(nativeCart.items) || nativeCart.items.length > 40 || !nativeCart.items.length) fail('CART');
    const paid = lines => lines.filter(line => !rewards.isOwnedGift(line));
    if (!sameQuantities(paid(before.items), paid(nativeCart.items))) fail('CART_CHANGED');
    const currency = String(nativeCart.currency || '').toUpperCase();
    if (!/^[A-Z]{3}$/.test(currency)) fail('CART');
    const lines = nativeCart.items.map(line => ({
      merchandiseId: gid('ProductVariant', variantOf(line)),
      quantity: quantityOf(line),
      attributes: attributesOf(line),
      ...(planOf(line) ? { sellingPlanId: gid('SellingPlan', planOf(line)) } : {})
    }));
    const nativeCustomerCodes = activeCustomerCodes(nativeCart);
    const nativeActive = new Set(nativeCustomerCodes.map(code => code.toUpperCase()));
    if (originalCustomerCodes.some(code => !nativeActive.has(code.toUpperCase()))) fail('CART');
    const activeOtherCodes = codesOf([...originalCustomerCodes, ...nativeCustomerCodes]);
    const discountCodes = codesOf([
      ...codesOf(nativeCart.discount_codes).filter(code => !ownCodes.has(code.toUpperCase())), ...activeOtherCodes,
      ...benefit.codes
    ]);
    const input = {
      lines, discountCodes,
      attributes: Object.entries(nativeCart.attributes || {}).map(([key, value]) => ({ key, value: String(value) })),
      ...(typeof nativeCart.note === 'string' && nativeCart.note ? { note: nativeCart.note } : {}),
      buyerIdentity: { countryCode: /^[A-Z]{2}$/.test(window.Shopify?.country || '') ? window.Shopify.country : 'US' }
    };
    return createAuthority(body, input, benefit, config, currency, activeOtherCodes, nativeCart);
  }

  async function assertNativeUnchanged(nativeCart, input) {
    const { rewards } = configuration();
    const current = await rewards.readCart();
    if (current.token !== nativeCart.token || !sameQuantities(current.items, nativeCart.items) ||
      String(current.note || '') !== String(nativeCart.note || '') ||
      stableAttributes(Object.entries(current.attributes || {}).map(([key, value]) => ({key, value:String(value)}))) !== stableAttributes(input.attributes) ||
      codesOf(current.discount_codes).map(code => code.toUpperCase()).sort().join(',') !== codesOf(nativeCart.discount_codes).map(code => code.toUpperCase()).sort().join(',')) fail('CART_CHANGED');
  }

  async function createAuthority(body, input, benefit, config, currency, activeOtherCodes, nativeCart) {
    const lines = input.lines;
    const response = await fetchStorefront('/api/2026-07/graphql.json', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: createQuery, variables: { input, first: Math.min(50, lines.length + 5) } })
    });
    let json;
    try { json = await response.json(); } catch { fail('CART'); }
    if (!response.ok || json.errors?.length || json.data?.cartCreate?.userErrors?.length || json.data?.cartCreate?.warnings?.length) fail('CART');
    const verified = verifyStorefront(json.data?.cartCreate?.cart, lines.map(line => ({ ...line, variantId: line.merchandiseId })), benefit, config, currency);
    const applied = new Set(applicableCodes(verified.cart).map(code => code.toUpperCase()));
    if (activeOtherCodes.some(code => !applied.has(code.toUpperCase())) ||
      stableAttributes(verified.cart.attributes || []) !== stableAttributes(input.attributes) ||
      input.note && verified.cart.note !== input.note) fail('CART');
    const originals = new Map(body.cart.items.map(line => [logicalIdentity(line), line]));
    const items = verified.lines.map(line => {
      const variant = line.merchandise, plan = line.sellingPlanAllocation?.sellingPlan;
      const previous = originals.get(logicalIdentity(line)) || {};
      const linePrice = monetary(line.cost.totalAmount, currency);
      const originalLinePrice = monetary(line.cost.subtotalAmount, currency);
      const title = previous.title === variant.title + ' — ' + variant.product.title ? previous.title :
        variant.product.title + (variant.title && variant.title !== 'Default Title' ? ' - ' + variant.title : '');
      return {
        ...previous, variantId: variant.id, productId: variant.product.id,
        quantity: line.quantity, title, variant: variant.title || 'Default Title',
        originalPrice: originalLinePrice / line.quantity, price: linePrice / line.quantity,
        linePrice, totalDiscount: Math.max(0, originalLinePrice - linePrice),
        image: variant.image?.url || variant.product.featuredImage?.url || '', url: '/products/' + variant.product.handle,
        weight: Number(variant.weight) || 0, weightUnit: variant.weightUnit || 'GRAMS',
        currencyCode: currency, properties: Object.fromEntries(attributesOf(line).map(attribute => [attribute.key, attribute.value])),
        sellingPlanId: plan ? idOf(plan.id) : null, sellingPlanName: plan?.name || null,
        sellingPlanGroupId: plan ? previous.sellingPlanGroupId || '' : ''
      };
    });
    const originalTotal = verified.lines.reduce((sum, line) => sum + monetary(line.cost.subtotalAmount, currency), 0);
    const totalDiscount = [...(verified.cart.discountAllocations || []), ...verified.lines.flatMap(line => line.discountAllocations || [])]
      .reduce((sum, allocation) => sum + monetary(allocation.discountedAmount, currency), 0);
    const nextBody = {
      ...body, checkoutId: '',
      cart: {
        ...body.cart, items, cartToken: verified.cart.id,
        itemCount: verified.cart.totalQuantity, totalPrice: verified.total,
        originalTotalPrice: originalTotal, totalDiscount,
        totalWeight: items.reduce((sum, line) => sum + line.weight * line.quantity * ({ GRAMS: 1, KILOGRAMS: 1000, OUNCES: 28.349523125, POUNDS: 453.59237 }[line.weightUnit] || 1), 0),
        currency: currency, currencyCode: currency, discountCodes: applicableCodes(verified.cart)
      },
      extra: { ...body.extra, corlynaRewards: '2026-09-08', corlynaRewardUnits: benefit.units }
    };
    delete nextBody.cart.corlynaDirectSelection;
    await assertNativeUnchanged(nativeCart, input);
    return { body: nextBody, verified, input, activeOtherCodes, nativeCart };
  }

  function unwrap(response) { return object(response?.data) ? response.data : response; }
  function verifyHosted(raw, verified, config, options = {}) {
    const data = unwrap(raw), costs = data?.costs, lines = data?.cart?.items;
    if (!object(costs) || !Array.isArray(lines) || !sameQuantities(verified.lines, lines, logicalIdentity)) fail('CHECKOUT');
    if (String(costs.currencyCode || '').toUpperCase() !== verified.currency) fail('CHECKOUT');
    for (const field of ['total', 'subtotal', 'shipping']) if (costs[field] == null || !Number.isFinite(Number(costs[field])) || Number(costs[field]) < 0) fail('CHECKOUT');
    const codes = new Set(codesOf(data.discountCodes || data.discountCode || data.checkout?.discount?.discountCode).map(code => code.toUpperCase()));
    if (verified.benefit.codes.some(code => !codes.has(code.toUpperCase()))) fail(verified.benefit.gift ? 'GIFT' : 'SHIPPING');
    if (applicableCodes(verified.cart).some(code => !codes.has(code.toUpperCase()))) fail('CART');
    if (!options.amountPending && verified.benefit.freeShipping && !near(Number(costs.shipping), 0)) fail('SHIPPING');
    const hostedTax = Number(costs.tax || 0), tip = Number(costs.tipping || 0);
    if (!options.amountPending && !near(Number(costs.total), verified.total - verified.tax + hostedTax + tip + Number(costs.shipping))) fail('CHECKOUT');
    if (verified.benefit.gift) {
      const gifts = lines.filter(line => variantOf(line) === String(config.gift.id));
      const expected = verified.lines.filter(line => variantOf(line) === String(config.gift.id))
        .reduce((sum, line) => sum + monetary(line.cost.totalAmount, verified.currency), 0);
      if (!gifts.length || gifts.some(line => line.price == null || !Number.isFinite(Number(line.price)) || Number(line.price) < 0)) fail('GIFT');
      // Current hosted summaries expose `price`; different payload readers
      // expose a unit or a line amount. Require the actual gift SKU total to
      // match the authoritative cart using one complete representation.
      const totals = [gifts.reduce((sum, line) => sum + Number(line.price), 0),
        gifts.reduce((sum, line) => sum + Number(line.price) * line.quantity, 0)];
      if (gifts.every(line => line.linePrice != null && Number.isFinite(Number(line.linePrice)))) totals.push(gifts.reduce((sum, line) => sum + Number(line.linePrice), 0));
      if (!totals.some(total => near(total, expected))) fail('GIFT');
    }
    return true;
  }

  function creditRate(raw, config) {
    const data = unwrap(raw), credit = config.shippingCredit;
    if (!object(credit) || !String(credit.code || '').trim() || credit.currency !== 'USD' ||
      !Number.isFinite(Number(credit.amount)) || Number(credit.amount) <= 0 ||
      data?.costs?.currencyCode !== 'USD' || !Array.isArray(data.regions) || data.regions.length !== 1 || data.regions[0].code !== 'US') fail('SHIPPING');
    const methods = data.shippingList || data.costs?.shippingList;
    if (!Array.isArray(methods) || methods.length !== 1) fail('SHIPPING');
    const method = methods[0], amount = Number(data.costs.shipping);
    if (String(method.name || method.label || '').trim() !== 'Express' || !method.id || method.type !== 'shopify' ||
      !near(Number(method.price ?? method.amount), amount) || !near(amount, Number(credit.amount))) fail('SHIPPING');
    return { amount, method: { id: String(method.id), type: method.type }, credit };
  }

  async function addShippingCredit(prepared, raw, config) {
    const rate = creditRate(raw, config), initial = prepared.verified;
    const creditCode = rate.credit.code.trim(), shippingCode = String(config.codes?.shipping || '').toUpperCase();
    if (initial.currency !== 'USD' || !initial.benefit.freeShipping || !shippingCode ||
      applicableCodes(initial.cart).some(code => code.toUpperCase() === creditCode.toUpperCase())) fail('SHIPPING');
    const removeShipping = codes => codesOf(codes).filter(code => code.toUpperCase() !== shippingCode);
    const retained = removeShipping(applicableCodes(initial.cart));
    const benefit = { ...initial.benefit, codes: codesOf([...removeShipping(initial.benefit.codes), creditCode]) };
    const input = { ...prepared.input, discountCodes: codesOf([...removeShipping(prepared.input.discountCodes), creditCode]),
      buyerIdentity: { countryCode: 'US' } };
    const next = await createAuthority(prepared.body, input, benefit, config, initial.currency, retained, prepared.nativeCart);
    const allocations = [...(next.verified.cart.discountAllocations || []), ...next.verified.lines.flatMap(line => line.discountAllocations || [])];
    const creditAmount = allocations.filter(allocation => String(allocation.code || '').toUpperCase() === creditCode.toUpperCase())
      .reduce((sum, allocation) => sum + monetary(allocation.discountedAmount, initial.currency), 0);
    if (!near(creditAmount, rate.amount) || !near(next.verified.total, initial.total - rate.amount) ||
      !near(next.verified.tax, initial.tax)) fail('SHIPPING');
    return { ...next, creditRate: rate, originalTotal: initial.total, originalTax: initial.tax };
  }

  async function calculateCreditQuote(client, original, prepared, created, raw, config, headers) {
    // GET exposes a pre-discount total for order discounts. Its real SKU/plan,
    // code and gift fields still have to match. Only calculate-costs supplies
    // the authoritative discounted quote; no locally recomputed total is used.
    verifyHosted(raw, prepared.verified, config, { amountPending: true });
    const data = unwrap(raw), rate = creditRate(raw, config);
    if (!near(rate.amount, prepared.creditRate.amount)) fail('SHIPPING');
    const response = await original.call(client, '/api/checkout/calculate-costs', {
      checkoutId: unwrap(created).checkoutId, cartToken: prepared.verified.cart.id,
      shipping: { countryCode: 'US' }, subtotal: Number(data.costs.subtotal),
      discountCode: applicableCodes(prepared.verified.cart).join(','), shippingMethod: rate.method
    }, headers);
    if (response?.code != null && response.code !== 200) fail('SHIPPING');
    const quote = unwrap(response), costs = quote?.costs || quote;
    if (!object(costs) || costs.currencyCode !== prepared.verified.currency) fail('SHIPPING');
    for (const key of ['subtotal', 'total', 'shipping']) if (costs[key] == null || !Number.isFinite(Number(costs[key])) || Number(costs[key]) < 0) fail('SHIPPING');
    const tax = Number(costs.tax || 0), tip = Number(costs.tipping || 0);
    if (!Number.isFinite(tax) || tax < 0 || !Number.isFinite(tip) || tip < 0 ||
      !near(Number(costs.shipping), rate.amount) ||
      !near(Number(costs.subtotal), prepared.verified.total - prepared.verified.tax) ||
      !near(Number(costs.total), prepared.originalTotal - prepared.originalTax + tax + tip)) fail('SHIPPING');
    const methods = quote.shippingList || costs.shippingList;
    if (Array.isArray(methods) && methods.length && (methods.length !== 1 ||
      String(methods[0].id) !== rate.method.id || !near(Number(methods[0].price ?? methods[0].amount), rate.amount))) fail('SHIPPING');
    await assertNativeUnchanged(prepared.nativeCart, prepared.input);
    return response;
  }

  async function createAndRead(client, original, path, prepared, args) {
    await assertNativeUnchanged(prepared.nativeCart, prepared.input);
    const nextArgs = args.slice(); nextArgs[0] = path; nextArgs[1] = prepared.body;
    const response = await original.apply(client, nextArgs), checkoutId = unwrap(response)?.checkoutId;
    if (response?.code != null && response.code !== 200 || !checkoutId || typeof client.get !== 'function') fail('CHECKOUT');
    const read = await client.get('/api/checkout/' + encodeURIComponent(checkoutId));
    if (read?.code != null && read.code !== 200) fail('CHECKOUT');
    return { response, read };
  }
  let active = false;
  async function checkoutPost(client, original, args) {
    if (!creationPaths.has(args[0]) || !object(args[1]?.cart) || !Array.isArray(args[1].cart.items)) return original.apply(client, args);
    const { config } = configuration();
    const body = args[1];
    if (!config.enabled || !creationPaths.has(args[0]) || !object(body?.cart) || !Array.isArray(body.cart.items) ||
      !body.cart.items.some(line => config.variants?.[variantOf(line)] || variantOf(line) === String(config.gift?.id))) return original.apply(client, args);
    if (active) fail('BUSY');
    active = true;
    try {
      clearError();
      let prepared = await preparePayload(body);
      if (prepared.bypass) return original.apply(client, args);
      if (!prepared.verified.benefit.freeShipping) {
        const direct = await createAndRead(client, original, args[0], prepared, args);
        verifyHosted(direct.read, prepared.verified, config);
        await assertNativeUnchanged(prepared.nativeCart, prepared.input);
        completedDirectCart = prepared.body.cart; return direct.response;
      }
      // Express routes may create a payment-provider order. Finish every
      // discount and cost check using standard unpaid checkouts first.
      let standard = await createAndRead(client, original, '/api/checkout', prepared, args);
      verifyHosted(standard.read, prepared.verified, config, { amountPending: true });
      if (near(Number(unwrap(standard.read).costs.shipping), 0)) {
        verifyHosted(standard.read, prepared.verified, config);
      } else {
        prepared = await addShippingCredit(prepared, standard.read, config);
        standard = await createAndRead(client, original, '/api/checkout', prepared, args);
        await calculateCreditQuote(client, original, prepared, standard.response, standard.read, config, args[2]);
      }
      if (args[0] === '/api/checkout') {
        await assertNativeUnchanged(prepared.nativeCart, prepared.input);
        completedDirectCart = prepared.body.cart; return standard.response;
      }
      const express = await createAndRead(client, original, args[0], prepared, args);
      if (prepared.creditRate) await calculateCreditQuote(client, original, prepared, express.response, express.read, config, args[2]);
      else {
        verifyHosted(express.read, prepared.verified, config);
        await assertNativeUnchanged(prepared.nativeCart, prepared.input);
      }
      completedDirectCart = prepared.body.cart; return express.response;
    } catch (error) {
      showError(error);
      if (error?.name === 'CorlynaRewardsError') throw error;
      fail('CHECKOUT');
    } finally { active = false; }
  }
  let attempts = 0;
  function install() {
    let client;
    try { client = typeof yxRequest === 'undefined' ? null : yxRequest; } catch { client = null; }
    if (!client || typeof client.post !== 'function' || !window.CorlynaRewards) {
      if (++attempts < 120) setTimeout(install, 250);
      return false;
    }
    if (client.post.corlynaRewardsCheckout) return true;
    const original = client.post;
    function rewardsPost(...args) { return checkoutPost(this, original, args); }
    Object.defineProperty(rewardsPost, 'corlynaRewardsCheckout', { value: true });
    // Retain the title adapter marker so its delayed installer does not wrap
    // itself twice. The original adapter remains in this delegation chain.
    if (original.corlynaPackageDisplay) Object.defineProperty(rewardsPost, 'corlynaPackageDisplay', { value: true });
    client.post = rewardsPost;
    return true;
  }
  window.CorlynaRewardsCheckout = { install, preparePayload, prepareDirectPayload, selectionFromProduct, captureProductCheckout, directCheckout, verifyStorefront, verifyHosted, createQuery, readQuery };
  if (!window.__CORLYNA_REWARDS_CHECKOUT_TEST_NO_INIT) { window.addEventListener('click', captureProductCheckout, true); install(); }
})();
